import { useTranslator } from "@eo-locale/react";

import trim from "lodash/trim";

import { useEffect, useState } from "react";

import {
  FormControl,
  SingleLineQuestion,
  EssayQuestion,
  ColourOrImageQuestion
} from "@/storybook-components";

import RequiredField from "@/components/questions/required-field";
import useUser from "@/hooks/useUser";
import { useAppSelector } from "@/redux/store/hooks";
import PreviewCard from "../../common/preview-card";
import { scrollIntoView } from "@/lib";

import Alerts from "@/components/alerts";

const CreateIdea = ({
  postData,
  setPostData,
  questionError,
  setQuestionError,
  hasErrorInSubmission,
  ideaSubmitting,
  isEditPost,
  currentPostData,
  onSubmit
}): JSX.Element => {
  const { translate } = useTranslator();
  const { isRegisteredUserLoggedIn } = useUser();
  const { backgroundColours } = useAppSelector(
    (state) => state.toolCustomizations
  );
  const { registeredUser, unverifiedUser } = useAppSelector(
    (state) => state.user
  );
  const { brainstormer } = useAppSelector((state) => state.brainstormers);

  const [hasBackgroundColorNote, setHasBackgroundColorNote] = useState(true);

  useEffect(() => {
    if (isEditPost) {
      setPostData({
        ...postData,
        name: currentPostData?.name,
        description: currentPostData?.description,
        mediaUrl: currentPostData?.mediaUrl,
        backgroundColourId: currentPostData?.backgroundColourId
      });
    }
  }, []);

  const handleTitleChange = (text: string) => {
    const trimmedText = trim(text);
    setPostData({ ...postData, name: trimmedText });
    setQuestionError({ ...questionError, name: "" });

    if (trimmedText === "") {
      setQuestionError({ ...questionError, name: "blank" });
    }
  };

  const validateAndSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    // Validate required fields
    let hasErrors = false;
    const newErrors = { ...questionError };

    // Validate title/name
    if (!postData?.name || trim(postData.name) === "") {
      newErrors.name = "blank";
      hasErrors = true;
    }

    // Validate style (background color or image)
    if (!postData?.backgroundColourId && !postData?.mediaUrl) {
      newErrors.backgroundColourId = "empty";
      newErrors.mediaUrl = "empty";
      hasErrors = true;
    }

    // Update errors
    setQuestionError(newErrors);

    // If there are errors, scroll to the first error and focus
    if (hasErrors) {
      // Use setTimeout to ensure DOM has updated with error classes
      setTimeout(() => {
        const errorElement = document.querySelector(".ehq-question-error");
        if (errorElement) {
          // Focus on the first input, button, or textarea within the error element
          const focusableElement = errorElement.querySelector(
            "input, button, textarea, select"
          ) as HTMLElement;

          if (focusableElement) {
            focusableElement.focus();
          }

          // Scroll to the error element
          scrollIntoView(".ehq-question-error");
        }
      }, 100);
      return false;
    }

    // If no errors and onSubmit is provided, call it
    if (onSubmit) {
      return onSubmit(e);
    }

    return true;
  };

  const previewNode = () => {
    const userName = registeredUser?.profile.login || unverifiedUser?.login;
    if (
      postData?.name &&
      (postData.backgroundColourId || postData.mediaUrl) &&
      (isRegisteredUserLoggedIn || userName)
    ) {
      return (
        <PreviewCard
          data={postData}
          username={userName}
          avatarURL={registeredUser?.profile?.imageUrl}
          showCommentIcon={brainstormer?.allowCommentsForIdeas}
        />
      );
    }
  };

  return (
    <FormControl
      as="form"
      onSubmit={validateAndSubmit}
      display="flex"
      flexDirection="column"
      isDisabled={ideaSubmitting}
      px={4}
      gap={6}
      sx={{
        "> p": { margin: 0 },
        "> div": { margin: 0 },
        ".fr-view": {
          fontSize: "sm",
          fontWeight: "semibold",
          color: "gray.800"
        },
        ".create-idea-form": {
          fontWeight: "normal"
        },
        "& input, & textarea": {
          background: "white"
        }
      }}
      /**
       * 'fr-view' is used because of HTMLWrapper
       * TO DO: remove HTMLWrapper implementation for create ideas
       * */
    >
      {isEditPost &&
        hasBackgroundColorNote &&
        !currentPostData?.backgroundColourId &&
        !currentPostData?.mediaUrl && (
          <Alerts
            status="info"
            description={translate("brainstormers.nowChooseBgColourforIdea")}
            onClose={() => setHasBackgroundColorNote(false)}
          />
        )}
      <RequiredField />
      <div className={questionError?.name ? "ehq-question-error" : ""}>
        <SingleLineQuestion
          id="1"
          status={ideaSubmitting ? "saving" : "idle"}
          question={translate("brainstormers.addYourText")}
          required
          hasMaxLength
          maxLength={140}
          notes={translate("common.MaxLengthHelper", {
            count: 140
          })}
          onChangeHandler={handleTitleChange}
          error={questionError?.name}
          existingValue={isEditPost && currentPostData?.name}
        />
      </div>
      <div
        className={
          questionError?.backgroundColourId || questionError?.mediaUrl
            ? "ehq-question-error"
            : ""
        }
      >
        <ColourOrImageQuestion
          id="2"
          required
          status={ideaSubmitting ? "saving" : "idle"}
          question={translate("brainstormers.style")}
          notes={
            brainstormer.allowFileUploadForIdeas
              ? translate("brainstormers.styleNote")
              : translate("brainstormers.styleColourNote")
          }
          backgroundColours={backgroundColours}
          onColourChangeHandler={(colourId: number) => {
            setQuestionError({
              ...questionError,
              backgroundColourId: "",
              mediaUrl: ""
            });
            setPostData({ ...postData, backgroundColourId: colourId });
          }}
          allowImageUpload={brainstormer.allowFileUploadForIdeas}
          onImageChangeHandler={(imageUrl: string) => {
            setQuestionError({
              ...questionError,
              backgroundColourId: "",
              mediaUrl: ""
            });

            if (!imageUrl) {
              setPostData({
                ...postData,
                mediaUrl: "",
                backgroundColourId: ""
              });
              setQuestionError({ ...questionError, mediaUrl: "empty" });
              return;
            }

            setPostData({
              ...postData,
              mediaUrl: imageUrl,
              backgroundColourId: ""
            });
          }}
          error={questionError?.backgroundColourId || questionError?.mediaUrl}
          existingIdOrUrl={
            isEditPost && {
              color: currentPostData?.backgroundColourId,
              image: currentPostData?.mediaUrl
            }
          }
        />
      </div>
      <EssayQuestion
        id="3"
        status={ideaSubmitting ? "saving" : "idle"}
        question={translate("brainstormers.addMoreDetails")}
        onChangeHandler={(text) => {
          setPostData({ ...postData, description: trim(text) });
        }}
        existingValue={isEditPost && currentPostData?.description}
      />

      {/* Error(s) alert message */}
      {hasErrorInSubmission &&
        isRegisteredUserLoggedIn &&
        !(
          postData?.name &&
          (postData.backgroundColourId || postData.mediaUrl)
        ) && (
          <Alerts
            status="error"
            description={translate("errors.pleaseProvideRequiredInfo")}
          />
        )}

      {hasErrorInSubmission &&
        !isRegisteredUserLoggedIn &&
        !(
          postData?.name &&
          (postData.backgroundColourId || postData.mediaUrl)
        ) && (
          <Alerts
            status="error"
            description={translate("errors.pleaseProvideRequiredInfo")}
          />
        )}
      {previewNode()}
    </FormControl>
  );
};

export default CreateIdea;
